import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Chip, CircularProgress, Grid, Avatar, IconButton, Tooltip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Notifications as NotifIcon, Send, Refresh, People, Person,
  School, CheckCircle, ErrorOutline, AutoAwesome, Cake, BeachAccess,
  Assignment, MenuBook, ChildCare, AttachMoney, PlayArrow, AddPhotoAlternate,
  Close, PictureAsPdf, VideoLibrary, Image as ImageIcon,
} from '@mui/icons-material';
import { InvokeApi, apiList, postImg } from '../../services/ApiService';
import { Config } from '../../config/Config';
import AsyncStorage from '../../services/AsyncStorage';
import dayjs from 'dayjs';

const TYPE_OPTS = ['general', 'Attendance', 'Leave', 'Fee', 'Holiday', 'Assignment', 'Notification'];

const TYPE_STYLE = {
  general:    { bg: '#e8eaf6', color: '#1a237e' },
  Attendance: { bg: '#e8f5e9', color: '#2e7d32' },
  Leave:      { bg: '#fff3e0', color: '#e65100' },
  Fee:        { bg: '#e0f2f1', color: '#00695c' },
  Holiday:    { bg: '#fce4ec', color: '#880e4f' },
  Assignment: { bg: '#f3e5f5', color: '#6a1b9a' },
  Notification: { bg: '#e8eaf6', color: '#1a237e' },
};

// ── Auto Notifications Panel ──────────────────────────────────────────────────
const AUTO_ACTIONS = [
  { key: 'triggerAllAutoNotifications', label: 'Trigger All (Force)', icon: <AutoAwesome />, color: '#1a237e', bg: '#e8eaf6', force: true },
  { key: 'sendAutoBirthdayNotifications', label: 'Birthday', icon: <Cake />, color: '#6a1b9a', bg: '#f3e5f5' },
  { key: 'sendHolidayReminders', label: 'Holiday Reminders', icon: <BeachAccess />, color: '#00695c', bg: '#e0f2f1' },
  { key: 'sendAssignmentReminders', label: 'Assignment Reminders', icon: <Assignment />, color: '#e65100', bg: '#fff3e0' },
  { key: 'sendNewStoryNotifications', label: 'Story Notifications', icon: <MenuBook />, color: '#1565c0', bg: '#e3f2fd' },
  { key: 'sendKidsActivitiesNotifications', label: 'Kids Activities', icon: <ChildCare />, color: '#2e7d32', bg: '#e8f5e9' },
  { key: 'sendFeesReminder', label: 'Fees Reminder', icon: <AttachMoney />, color: '#c62828', bg: '#ffebee' },
];

const AutoNotificationsPanel = () => {
  const [firing, setFiring] = useState({});
  const [results, setResults] = useState({});

  const fire = async (action) => {
    const url = action.force
      ? `${Config.main_url}/${action.key}?force=1`
      : `${Config.main_url}/${action.key}`;
    setFiring(p => ({ ...p, [action.key]: true }));
    setResults(p => ({ ...p, [action.key]: null }));
    try {
      const token = await AsyncStorage.getItem('token');
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setResults(p => ({ ...p, [action.key]: data }));
    } catch (e) {
      setResults(p => ({ ...p, [action.key]: { code: '500', message: e.message } }));
    }
    setFiring(p => ({ ...p, [action.key]: false }));
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <AutoAwesome sx={{ color: '#1a237e' }} />
          <Box>
            <Typography variant="h6" fontWeight={800} color="#1a237e">Auto Notifications</Typography>
            <Typography variant="body2" color="text.secondary" fontSize={12}>
              Manually trigger scheduled notifications
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={1.5}>
          {AUTO_ACTIONS.map(action => {
            const res = results[action.key];
            const isLoading = !!firing[action.key];
            const isSuccess = res?.code === '200';
            return (
              <Grid item xs={12} sm={6} md={4} key={action.key}>
                <Box sx={{
                  border: `1.5px solid ${res ? (isSuccess ? '#c8e6c9' : '#ffcdd2') : '#e8eaf6'}`,
                  borderRadius: 2, p: 1.5,
                  background: res ? (isSuccess ? '#f1f8e9' : '#fff8f8') : '#fafafa',
                  transition: 'all 0.2s',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: res ? 1 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 30, height: 30, background: action.bg, flexShrink: 0 }}>
                        {React.cloneElement(action.icon, { sx: { fontSize: 16, color: action.color } })}
                      </Avatar>
                      <Typography fontSize={12} fontWeight={700} color="#333">{action.label}</Typography>
                    </Box>
                    <Tooltip title={`Fire: ${action.label}`}>
                      <IconButton size="small" onClick={() => fire(action)} disabled={isLoading}
                        sx={{ background: action.bg, color: action.color, '&:hover': { background: action.bg, opacity: 0.8 } }}>
                        {isLoading ? <CircularProgress size={14} sx={{ color: action.color }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {res && (
                    <Box sx={{ mt: 0.5 }}>
                      {isSuccess ? (
                        <Box>
                          {(res.results || [res.result]).filter(Boolean).map((r, i) => (
                            <Typography key={i} fontSize={11} color="#2e7d32">
                              ✓ {r.type}: sent {r.sent ?? '—'}{r.failed ? `, failed ${r.failed}` : ''}{r.total ? ` / ${r.total}` : ''}
                            </Typography>
                          ))}
                          {!res.results && !res.result && (
                            <Typography fontSize={11} color="#2e7d32">✓ {res.message}</Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography fontSize={11} color="#c62828">✗ {res.message}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

const SendPanel = ({ classes, onSent }) => {
  const [mode, setMode] = useState('all');
  const [form, setForm] = useState({
    title: '', message: '', type: 'general',
    standard: '', section: '', student_id: '',
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaName, setMediaName] = useState('');
  const [mediaType, setMediaType] = useState(''); // 'image' | 'video' | 'pdf'

  const getMediaIcon = (type) => {
    if (type === 'video') return <VideoLibrary sx={{ fontSize: 14 }} />;
    if (type === 'pdf') return <PictureAsPdf sx={{ fontSize: 14 }} />;
    return <ImageIcon sx={{ fontSize: 14 }} />;
  };

  const handleMediaSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setMediaUploading(true);
    const fd = new FormData();
    fd.append('notification-media', file);
    const data = await postImg(Config.main_url + '/addNotificationMedia', fd);
    setMediaUploading(false);
    if (data?.code === '200') {
      setMediaName(data.mediaName);
      setMediaType(data.mediaType);
    } else {
      alert('Upload failed: ' + (data?.message || 'Unknown error'));
      setMediaFile(null); setMediaPreview(''); setMediaName(''); setMediaType('');
    }
  };

  const clearMedia = () => {
    setMediaFile(null); setMediaPreview(''); setMediaName(''); setMediaType('');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const classOptions = classes.map(c => ({
    value: `${c.standard?.trim()}||${c.section?.trim()}`,
    label: `${c.standard?.trim()} - ${c.section?.trim()}`,
  }));

  const handleClassChange = (val) => {
    if (val) {
      const [std, sec] = val.split('||');
      setForm(p => ({ ...p, standard: std, section: sec }));
    }
  };

  const handleSend = async () => {
    if (!form.title || !form.message) { alert('Title and message are required'); return; }
    if (mode === 'class' && (!form.standard || !form.section)) { alert('Please select a class'); return; }
    if (mode === 'single' && !form.student_id) { alert('Please enter Student ID'); return; }

    setSending(true);
    setResult(null);

    try {
      const token = await AsyncStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

      let url, payload;
      if (mode === 'all') {
        url = Config.main_url + apiList.sendBulkNotificationsAll;
        payload = { title: form.title, message: form.message, type: form.type, image_url: mediaName || null };
      } else if (mode === 'class') {
        url = Config.main_url + apiList.sendBulkNotificationsByStdSec;
        payload = { title: form.title, message: form.message, type: form.type, standard: form.standard, section: form.section, image_url: mediaName || null };
      } else {
        url = Config.main_url + apiList.sendStudentNotification;
        payload = { title: form.title, message: form.message, type: form.type, student_id: form.student_id, image_url: mediaName || null };
      }

      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const data = await response.json();

      if (data?.code === '200') {
        setResult({ success: true, message: data.message || 'Notification sent successfully!' });
        setForm({ title: '', message: '', type: 'general', standard: '', section: '', student_id: '' });
        clearMedia();
        onSent?.();
      } else {
        setResult({ success: false, message: data?.message || 'Failed to send notification' });
      }
    } catch (e) {
      setResult({ success: false, message: 'Network error. Please try again.' });
    }
    setSending(false);
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Send sx={{ color: '#1a237e' }} />
          <Typography variant="h6" fontWeight={800} color="#1a237e">Send Notification</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          {[
            { key: 'all',    label: 'All Students', icon: <People fontSize="small" /> },
            { key: 'class',  label: 'By Class',     icon: <School fontSize="small" /> },
            { key: 'single', label: 'Single Student', icon: <Person fontSize="small" /> },
          ].map(m => (
            <Box key={m.key} onClick={() => setMode(m.key)} sx={{
              display: 'flex', alignItems: 'center', gap: 0.8, px: 2, py: 1,
              borderRadius: 2, cursor: 'pointer', border: '1.5px solid',
              borderColor: mode === m.key ? '#1a237e' : '#e8eaf6',
              background: mode === m.key ? '#e8eaf6' : '#fff',
              transition: 'all 0.2s',
            }}>
              <Box sx={{ color: mode === m.key ? '#1a237e' : 'text.secondary' }}>{m.icon}</Box>
              <Typography fontSize={12} fontWeight={700} color={mode === m.key ? '#1a237e' : 'text.secondary'}>
                {m.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Grid container spacing={2}>
          {mode === 'class' && (
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Class *"
                value={form.standard && form.section ? `${form.standard}||${form.section}` : ''}
                onChange={e => handleClassChange(e.target.value)}>
                <MenuItem value="">— Select Class —</MenuItem>
                {classOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
          )}

          {mode === 'single' && (
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Student ID *" value={form.student_id} onChange={f('student_id')} />
            </Grid>
          )}

          <Grid item xs={12} sm={8}>
            <TextField fullWidth size="small" label="Title *" value={form.title} onChange={f('title')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Type" value={form.type} onChange={f('type')}>
              {TYPE_OPTS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Message *" value={form.message}
              onChange={f('message')} multiline rows={3} />
          </Grid>

          {/* Media upload */}
          <Grid item xs={12}>
            <Box sx={{ border: '1.5px dashed #c5cae9', borderRadius: 2, p: 1.5, background: '#fafbff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component="label" size="small" variant="outlined" disabled={mediaUploading}
                  startIcon={mediaUploading ? <CircularProgress size={14} /> : <AddPhotoAlternate />}
                  sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                  {mediaUploading ? 'Uploading...' : 'Add Image / Video / PDF'}
                  <input type="file" hidden accept="image/*,video/*,.pdf" onChange={handleMediaSelect} />
                </Button>
                {mediaFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    {mediaType === 'image' && (
                      <Box component="img" src={mediaPreview} alt="preview"
                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #e8eaf6', flexShrink: 0 }} />
                    )}
                    {mediaType === 'video' && (
                      <Box sx={{ width: 48, height: 48, borderRadius: 1.5, background: '#e8eaf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <VideoLibrary sx={{ color: '#1a237e', fontSize: 28 }} />
                      </Box>
                    )}
                    {mediaType === 'pdf' && (
                      <Box sx={{ width: 48, height: 48, borderRadius: 1.5, background: '#ffebee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <PictureAsPdf sx={{ color: '#c62828', fontSize: 28 }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getMediaIcon(mediaType)}
                        <Typography fontSize={11} fontWeight={600} color="#1a237e" noWrap>{mediaFile?.name}</Typography>
                      </Box>
                      <Typography fontSize={10} color="text.secondary">
                        {mediaType === 'image' ? 'Image' : mediaType === 'video' ? 'Video' : 'PDF'} will be attached to notification
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={clearMedia} sx={{ color: '#c62828', flexShrink: 0 }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography fontSize={11} color="text.secondary">
                    Optional — attach image, video, or PDF
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2, borderRadius: 2 }}
            icon={result.success ? <CheckCircle /> : <ErrorOutline />}>
            {result.message}
          </Alert>
        )}

        <Button fullWidth variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
          onClick={handleSend} disabled={sending}
          sx={{ mt: 2.5, background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, py: 1.2 }}>
          {sending ? 'Sending...' : `Send to ${mode === 'all' ? 'All Students' : mode === 'class' ? (form.standard ? `${form.standard} - ${form.section}` : 'Class') : `Student #${form.student_id || '?'}`}`}
        </Button>
      </CardContent>
    </Card>
  );
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [filterDate, setFilterDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyStudentId, setHistoryStudentId] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' });
      setClasses(data?.sections || []);
    };
    init();
  }, []);

  const loadByDate = useCallback(async () => {
    setLoading(true);
    const d = dayjs(filterDate);
    const data = await InvokeApi(Config.main_url + apiList.getStudentNotificationsByDate, {
      year: d.year(), month: d.month() + 1, day: d.date(),
    });
    setNotifications(data?.notifications || []);
    setLoading(false);
  }, [filterDate]);

  useEffect(() => { loadByDate(); }, [loadByDate]);

  const loadHistory = async () => {
    if (!historyStudentId) { alert('Enter Student ID'); return; }
    setHistoryLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getStudentNotifications, {
      student_id: historyStudentId,
    });
    setHistoryData(data?.notifications || []);
    setHistoryLoading(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Notifications </Typography>
          <Typography variant="body2" color="text.secondary">Send and view student notifications </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setHistoryDialog(true)}
            startIcon={<NotifIcon />}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontWeight: 600 }}>
            Student History
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadByDate} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <AutoNotificationsPanel />

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <SendPanel classes={classes} onSent={loadByDate} />
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotifIcon sx={{ color: '#1a237e' }} />
                  <Typography variant="h6" fontWeight={800} color="#1a237e">Sent Notifications</Typography>
                </Box>
                <TextField type="date" size="small" value={filterDate}
                  onChange={e => setFilterDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} label="Date" sx={{ minWidth: 160 }} />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                  <CircularProgress sx={{ color: '#1a237e' }} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <NotifIcon sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight={500}>
                    No notifications on {dayjs(filterDate).format('DD MMM YYYY')}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    {notifications.length} notification{notifications.length !== 1 ? 's' : ''} sent
                  </Typography>
                  {notifications.map((n, i) => {
                    const style = TYPE_STYLE[n.type] || TYPE_STYLE.general;
                    return (
                      <Box key={n.ID || i}>
                        <Box sx={{ display: 'flex', gap: 2, py: 1.5, alignItems: 'flex-start' }}>
                          <Avatar sx={{ width: 38, height: 38, background: style.bg, flexShrink: 0 }}>
                            <NotifIcon sx={{ color: style.color, fontSize: 18 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.3 }}>
                              <Typography fontSize={13} fontWeight={700}>{n.title}</Typography>
                              <Chip label={n.type || 'general'} size="small"
                                sx={{ background: style.bg, color: style.color, fontWeight: 600, fontSize: 10, height: 18 }} />
                              <Chip label={` #${n.student_id} - ${n.student_name}`} size="small"
                                sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10, height: 18 }} />
                            </Box>
                            <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                              {n.message}
                            </Typography>
                            {n.image_url && (
                              <Box sx={{ mt: 0.8 }}>
                                {(!n.media_type || n.media_type === 'image') && (
                                  <Box component="img" src={n.image_url} alt="notification"
                                    sx={{ width: '100%', maxWidth: 200, maxHeight: 100,
                                          objectFit: 'cover', borderRadius: 1.5,
                                          border: '1px solid #e8eaf6' }} />
                                )}
                                {n.media_type === 'video' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <VideoLibrary sx={{ color: '#1a237e', fontSize: 18 }} />
                                    <Typography component="a" href={n.image_url} target="_blank" rel="noreferrer"
                                      fontSize={11} color="#1a237e" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                      View Video
                                    </Typography>
                                  </Box>
                                )}
                                {n.media_type === 'pdf' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <PictureAsPdf sx={{ color: '#c62828', fontSize: 18 }} />
                                    <Typography component="a" href={n.image_url} target="_blank" rel="noreferrer"
                                      fontSize={11} color="#c62828" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                      View PDF
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                            <Typography fontSize={10} color="text.disabled" mt={0.3}>
                              {n.created_at ? dayjs(n.created_at).format('hh:mm A') : '—'}
                            </Typography>
                          </Box>
                        </Box>
                        {i < notifications.length - 1 && <Divider />}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={historyDialog} onClose={() => setHistoryDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotifIcon /> Student Notification History
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" label="Student ID" value={historyStudentId}
              onChange={e => setHistoryStudentId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadHistory()} />
            <Button variant="contained" onClick={loadHistory} disabled={historyLoading}
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 80 }}>
              {historyLoading ? <CircularProgress size={18} color="inherit" /> : 'Search'}
            </Button>
          </Box>

          {historyData.length === 0 && !historyLoading ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              {historyStudentId ? 'No notifications found' : 'Enter a Student ID to search'}
            </Typography>
          ) : (
            historyData.map((n, i) => {
              const style = TYPE_STYLE[n.type] || TYPE_STYLE.general;
              return (
                <Box key={n.ID || i}>
                  <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, background: style.bg, flexShrink: 0 }}>
                      <NotifIcon sx={{ color: style.color, fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <Typography fontSize={13} fontWeight={700}>{n.title}</Typography>
                        <Chip label={n.type || 'general'} size="small"
                          sx={{ background: style.bg, color: style.color, fontWeight: 600, fontSize: 10, height: 18 }} />
                      </Box>
                      <Typography fontSize={12} color="text.secondary">{n.message}</Typography>
                      {n.image_url && (
                        <Box sx={{ mt: 0.8 }}>
                          {(!n.media_type || n.media_type === 'image') && (
                            <Box component="img" src={n.image_url} alt="notification"
                              sx={{ width: '100%', maxWidth: 180, maxHeight: 90,
                                    objectFit: 'cover', borderRadius: 1.5,
                                    border: '1px solid #e8eaf6' }} />
                          )}
                          {n.media_type === 'video' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <VideoLibrary sx={{ color: '#1a237e', fontSize: 18 }} />
                              <Typography component="a" href={n.image_url} target="_blank" rel="noreferrer"
                                fontSize={11} color="#1a237e" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                View Video
                              </Typography>
                            </Box>
                          )}
                          {n.media_type === 'pdf' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <PictureAsPdf sx={{ color: '#c62828', fontSize: 18 }} />
                              <Typography component="a" href={n.image_url} target="_blank" rel="noreferrer"
                                fontSize={11} color="#c62828" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                View PDF
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Typography fontSize={10} color="text.disabled" mt={0.3}>
                        {n.created_at ? dayjs(n.created_at).format('DD MMM YYYY hh:mm A') : '—'}
                      </Typography>
                    </Box>
                  </Box>
                  {i < historyData.length - 1 && <Divider />}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setHistoryDialog(false); setHistoryData([]); setHistoryStudentId(''); }}
            variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
